<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { formatMs } from '@/utils'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import { computed, ref, watch } from 'vue'
import type { DamageRankType, FightCoreRes, FightResType } from '@/types/fight'
import { fightExplain } from '@/core/fight'
import { monsterIcon } from '../data'
import EquipRoutine from '@/views/item/components/EquipRoutine.vue'
import FightInfoPanel from '@/views/fight/components/FightInfoPanel.vue'
import { useFightInfoPanel } from '@/views/fight/components/useFightInfoPanel'

const commonStore = useCommonStore()
const userStore = useUserStore()

const props = defineProps<{ monster: NestMonsterType | undefined; monsterCurHp: number; useTime?: number }>()
const emits = defineEmits(['fightOver', 'getTeamFightInfo'])

const monsterHp = ref(0)
const monsterCurHp = ref(0)
const monsterCurHpPercent = computed(() => (monsterCurHp.value / monsterHp.value) * 100)
const { fightMessageList, curMp, curCdList, curBuffInfoList, curDebuffInfoList, realUserAttr, pushFightMessage, applyFightRes, resetFightInfoPanel } =
  useFightInfoPanel()

const overTime = ref(0)
const isOver = ref(false)

const damageRank = ref<DamageRankType[]>([])

watch(
  () => props.monster,
  (val) => {
    if (!val) return
    monsterHp.value = val.hp
    monsterCurHp.value = props.monsterCurHp || val.hp
    if (['nest', 'runeRuin', 'crack', 'wild', 'relicRuin', 'skillCorridor', 'nian', 'plain'].includes(props.monster?.type || '')) {
      overTime.value = 180000 - (props?.useTime || 0)
    } else if (['runeTemple', 'advance', 'advanceTest', 'goldTemple', 'drageonMountain', 'peak', 'peakTest'].includes(props.monster?.type || '')) {
      overTime.value = 300000 - (props?.useTime || 0)
    }
    resetFightInfoPanel()
    damageRank.value = []
    isOver.value = false
    creatorId.value = undefined
  },
)
const fightRes = ref('fail')
const creatorId = ref<number>()
function fightOver(res: 'fail' | 'success', id?: number) {
  isOver.value = true
  if (res === 'fail') {
    overTime.value = 0
    fightRes.value = 'fail'
    ElMessage.error('您的团队副本挑战失败')
  } else {
    if (!props.monster) return
    fightRes.value = 'success'
    creatorId.value = id
    ElMessage.success('您的团队副本挑战成功')
  }
  if (props.monster?.type === 'wild') {
    emits('getTeamFightInfo')
    if (props.monsterCurHp === 0) {
      isOver.value = true
    }
  }
}
function cancelFight() {
  if (props.monster?.type === 'advance') {
    return ElMessageBox.confirm(`确定取消战斗吗？`, '提示', {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    })
      .then(cancelAction)
      .catch(() => {})
  }
  cancelAction()
  async function cancelAction() {
    await socketEmitAsync({ type: 'cancelTeamFight' })
    emits('fightOver', 'fail')
  }
}

watch(
  () => commonStore.isSocketInit,
  async (val) => {
    if (!val) return
    socketOn({
      type: 'teamFightCore',
      callBack: (res: FightCoreRes) => {
        monsterCurHp.value = res.monsterCurHp
        damageRank.value = res.damageRank.map((item, index) => ({
          rank: index + 1,
          ...item,
          percent: `${((item.damage / monsterHp.value) * 100).toFixed(2)}%`,
        }))
      },
    })
    socketOn({
      type: 'teamFightRes',
      callBack: (res: FightResType) => {
        if (['nest', 'runeRuin', 'crack', 'wild', 'relicRuin', 'skillCorridor', 'nian', 'plain'].includes(props.monster?.type || '')) {
          overTime.value = 180000 - res.useTime
        } else if (
          ['runeTemple', 'advance', 'advanceTest', 'goldTemple', 'drageonMountain', 'peak', 'peakTest'].includes(props.monster?.type || '')
        ) {
          overTime.value = 300000 - res.useTime
        }
        const fightText = fightExplain(res.atkInfoList, res.element)
        applyFightRes(res, fightText)
      },
    })
    socketOn({
      type: 'teamFightSuc',
      callBack: (res: number) => {
        pushFightMessage('挑战成功')
        monsterCurHp.value = 0
        fightOver('success', res)
      },
    })
    socketOn({
      type: 'teamFightFail',
      callBack: () => {
        pushFightMessage('挑战失败')
        fightOver('fail')
      },
    })
  },
)
</script>

<template>
  <div class="team-fight">
    <div class="monster-info">
      <MonsterDetail :monster="monster" :icon="monsterIcon[monster?.type || 'nest']" />
      <el-progress :text-inside="true" :stroke-width="20" :percentage="(monsterCurHpPercent < 0 ? 0 : monsterCurHpPercent) || 0" status="exception">
        <span>{{ monster && monsterCurHpPercent ? monsterCurHpPercent.toFixed(2) : 0 }} %</span>
      </el-progress>
      <p class="monster-extra-info" style="text-align: right">
        <span>{{ monsterCurHp }} / {{ monsterHp }}</span>
      </p>
    </div>
    <div class="fight-over-timer">
      {{ formatMs(overTime) }}
    </div>
    <div class="equip-routine-wrap">
      <span>快捷换装：</span>
      <EquipRoutine />
    </div>
    <div class="fight-user-list border-wrap">
      <h4>伤害排行</h4>
      <el-table
        :data="damageRank"
        border
        style="width: 100%; height: 20vh"
        :row-class-name="({ row }: any) => (row.id === userStore.id ? 'personal' : '')"
        size="small"
      >
        <el-table-column prop="rank" label="排位" width="50" />
        <el-table-column prop="nickname" label="昵称">
          <template #default="{ row }">
            <span class="nickname" :style="{ background: row.color }">{{ row.nickname }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="damage" label="伤害" width="80" />
        <el-table-column prop="percent" label="占比" width="70" />
      </el-table>
    </div>
    <FightInfoPanel
      :real-user-attr="realUserAttr"
      :buff-info-list="curBuffInfoList"
      :debuff-info-list="curDebuffInfoList"
      :fight-message-list="fightMessageList"
      :mp="curMp"
      :cd-list="curCdList"
      message-box-id="teamMessageBox"
    />
    <div class="fight-btn-wrap">
      <el-button class="main" type="danger" plain v-show="!isOver" @click="cancelFight" :disabled="damageRank.length > 1"> 取消挑战 </el-button>
      <el-button class="main" type="success" plain v-show="isOver" @click="emits('fightOver', fightRes, creatorId)" style="margin-left: 0"
        >返回</el-button
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
.team-fight {
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  .monster-info {
    :deep() {
      h2 {
        margin: 0;
      }
      button {
        margin-bottom: 10px;
      }
      .monster-detail {
        h3 {
          margin: 5px 0;
        }
        p {
          margin: 2px 0;
        }
      }
    }
  }
  .monster-extra-info {
    font-size: 12px;
    margin-top: 2px;
    display: flex;
    justify-content: space-between;
    padding: 0 5px;
  }
  .fight-user-list {
    h4 {
      display: block;
      font-size: 16px;
      text-align: center;
      margin: 10px 0;
      width: 100%;
    }
  }
  .fight-over-timer {
    background-color: rgba($color: #fff, $alpha: 0.2);
    font-weight: bold;
    border-radius: 5px;
    padding: 5px 10px;
    margin: 0 auto;
    margin-bottom: 10px;
  }
  .equip-routine-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    margin-bottom: 10px;
    span {
      flex-shrink: 0;
    }
    .el-select {
      margin-top: 0 !important;
    }
  }
  .fight-btn-wrap {
    margin: 10px 0;
    display: flex;
    .main {
      flex: 1;
    }
    .hour-list {
      display: flex;
      flex-flow: column nowrap;
      button {
        width: 100%;
        margin: 4px 0;
      }
    }
  }
}
</style>
